import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { getAuditLogsData } from "api";
import {
  COLORS,
  CustomMaterialTable,
  Loading,
  MarginWrapper,
  useTableExpandedRows,
  useYearMonthPeriodFilter,
  useYearPeriodFilter,
  EsgDatePickerField,
  BasicButton,
} from "components";

import { useNotify } from "hooks";
import { ParsedQueryParams } from "types";
import { useNewBreadcrumb } from "recoils";
import { getDateFormatset, isMoreThanOneMonthDifference } from "utils";

import { AUDIT_LOGS_PAGE, AUDIT_LOGS_COLUMNS_MAIN } from "./consts";
import { AuditLogsDetailsModal } from "./components";
import { dateRangeReducer, initialState } from "./helpers";

export const AuditLogsPage = () => {
  const [state, dispatch] = useReducer(dateRangeReducer, initialState);
  const [clearDate, setClearDate] = useState(false);
  const { isYearly, startDate, endDate, fromDate } = state;
  const [dateError, setDateError] = useState(false);
  const [isChanged, setChanged] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>();
  const notify = useNotify();

  useNewBreadcrumb(AUDIT_LOGS_PAGE.TITLE);
  const { setInitialExpanded, updateQueryParamsWithInitialExpanded } =
    useTableExpandedRows();

  const updateExternalStates = useCallback(() => {
    setInitialExpanded();
  }, [setInitialExpanded]);

  const getQueryParamsWithExternalChanges = useCallback(
    (queryParams: ParsedQueryParams) =>
      updateQueryParamsWithInitialExpanded(queryParams),
    [updateQueryParamsWithInitialExpanded],
  );
  const { period, availableYears } = useYearMonthPeriodFilter({
    updateExternalStates,
    getQueryParamsWithExternalChanges,
    isYearly,
  });

  const { year } = useYearPeriodFilter({
    updateExternalStates: undefined,
    withQueryParams: undefined,
    useYears: undefined,
  });

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();
  const maxDate = dayjs().utc();

  const {
    data: auditLogs,
    isLoading,
    mutate: auditLogsMutation,
  } = useMutation(getAuditLogsData);

  const tableData = auditLogs || [];

  const handleFromDateChange = (date: Dayjs | null) => {
    if (!date) return;
    const fromDateValue = date.format("YYYY-MM-DD");
    dispatch({ type: "SET_FROM_DATE", payload: { value: fromDateValue } });
    setChanged(true);
  };

  const handleToDateChange = (date: Dayjs | null) => {
    if (!date) return;
    const toDateValue = date.format("YYYY-MM-DD");
    dispatch({ type: "SET_TO_DATE", payload: { value: toDateValue } });
    if (fromDate && toDateValue < fromDate) {
      setDateError(true);
    } else {
      setChanged(true);
      setDateError(false);
    }
  };

  const handleClearDates = () => {
    dispatch({ type: "CLEAR_DATES" });
    setDateError(false);
    setClearDate(true);
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
  };

  const handleSearch = () => {
    setChanged(false);

    if (isMoreThanOneMonthDifference(dayjs(startDate), dayjs(endDate))) {
      notify.error(
        "We're not able to search the audit log beyond a 30 day window. Please adjust your Start and End Dates and try again.",
      );
    } else {
      auditLogsMutation({
        fromDate: startDate,
        toDate: endDate,
      });
    }
  };

  const datePickerData = [
    {
      label: "Start Date",
      onAcceptFn: handleFromDateChange,
      value: dayjs(startDate),
    },
    {
      label: "End Date",
      onAcceptFn: handleToDateChange,
      value: dayjs(endDate),
    },
  ];

  useEffect(() => {
    dispatch({
      type: "SET_FROM_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_TO_DATE",
      payload: { value: period.format("YYYY-MM-DD") },
    });
    dispatch({
      type: "SET_START_AND_END_DATES",
      payload: {
        startDate: period.format("YYYY-MM-DD"),
        endDate: period.format("YYYY-MM-DD"),
      },
    });
    // eslint-disable-next-line
  }, [period, year]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MarginWrapper>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        {!isYearly && (
          <Stack direction="row" spacing={2}>
            {datePickerData.map((obj) => (
              <EsgDatePickerField
                key={obj.label}
                width="160px"
                label={obj.label}
                openTo="day"
                minDate={minDate}
                maxDate={maxDate}
                views={["year", "month", "day"]}
                format={getDateFormatset()}
                disableFuture
                onChange={() => {}}
                onAccept={obj.onAcceptFn}
                value={obj.value}
                clearDate={clearDate}
                emission
              />
            ))}
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <BasicButton
          variant={isChanged ? "contained" : "outlined"}
          sx={{
            width: "120px",
            color: isChanged ? "white" : COLORS.Romance,
            marginRight: "0px",
          }}
          onClick={handleSearch}
        >
          Search
        </BasicButton>
        <BasicButton
          variant="outlined"
          sx={{ width: "120px", color: COLORS.Romance }}
          onClick={handleClearDates}
        >
          Clear Dates
        </BasicButton>
      </Box>
      <div>
        {isLoading ? (
          <Loading />
        ) : (
          <CustomMaterialTable
            columns={AUDIT_LOGS_COLUMNS_MAIN}
            data={tableData}
            enablePagination={false}
            containerSx={{
              height: "calc(100vh - 250px)",
              width: "calc(100vw - 150px)",
              overflowX: "auto",
            }}
            bodyCellSx={{ whiteSpace: "nowrap" }}
            rowClickable
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                setSelectedRowData(row.original);
              },
              sx: {
                maxHeight: "76px",
              },
            })}
          />
        )}
        {!!selectedRowData && (
          <AuditLogsDetailsModal
            isOpen
            data={selectedRowData}
            handleClose={() => setSelectedRowData(undefined)}
          />
        )}
      </div>
    </MarginWrapper>
  );
};

export default AuditLogsPage;
